import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Bookmark, ExternalLink, Trash2, Loader2, ArrowUpDown, Play, Eye, Search, X, Share2, MessageCircle, ImageOff } from "lucide-react";
import { toast } from "sonner";
import api, { imageUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { buildItemUrl } from "@/lib/shop";
import BrandName from "@/components/BrandName";
import MobileCreateButton from "@/components/MobileCreateButton";
import StarRating from "@/components/StarRating";
import AudienceBadge from "@/components/AudienceBadge";

const formatViews = (n) => {
  n = n || 0;
  if (n >= 1000000) return `${(n / 1000000).toFixed(1).replace(/\.0$/, "")}M`;
  if (n >= 1000) return `${(n / 1000).toFixed(1).replace(/\.0$/, "")}K`;
  return `${n}`;
};

const STYLE_META = {
  howto: { e: "📋", l: "How-To" },
  lecture: { e: "🎓", l: "Lecture" },
  documentary: { e: "🎬", l: "Documentary" },
  storytelling: { e: "📖", l: "Story" },
  coach: { e: "🔥", l: "Coach" },
  casual: { e: "☕", l: "Casual" },
  brief: { e: "💼", l: "Brief" },
  showhost: { e: "🎤", l: "Show" },
  advice: { e: "🧭", l: "Advice" },
  news: { e: "📰", l: "News" },
};

export default function BuyLater() {
  const navigate = useNavigate();
  const { user, refreshSavedCount } = useAuth();
  const [items, setItems] = useState(null);
  const [videos, setVideos] = useState(null);
  const [amazonTag, setAmazonTag] = useState("");
  const [sort, setSort] = useState("newest");
  const [vQuery, setVQuery] = useState("");

  useEffect(() => {
    api.get("/config").then(({ data }) => setAmazonTag(data.amazon_affiliate_tag || "")).catch(() => {});
    api.get("/saved-items")
      .then(({ data }) => { setItems(data.items || []); refreshSavedCount(); })
      .catch(() => setItems([]));
    api.get("/saved-videos")
      .then(({ data }) => setVideos(data.videos || []))
      .catch(() => setVideos([]));
  }, [refreshSavedCount]);

  const removeVideo = async (mid) => {
    setVideos((cur) => (cur || []).filter((v) => v.manual_id !== mid));
    try {
      await api.delete(`/saved-videos/${mid}`);
      refreshSavedCount();
    } catch {
      toast.error("Couldn't remove that video");
    }
  };

  const shareCard = async (e, v) => {
    e.stopPropagation();
    const url = `${window.location.origin}/api/v/${v.manual_id}`;
    const title = v.title || "Vittyyo video";
    try {
      if (navigator.share) { await navigator.share({ title, url }); return; }
    } catch (err) { if (err?.name === "AbortError") return; }
    try { await navigator.clipboard.writeText(url); toast.success("Link copied — share it anywhere!"); }
    catch { toast.error("Couldn't copy the link"); }
  };

  const remove = async (id) => {
    setItems((cur) => cur.filter((it) => it.id !== id));
    try {
      await api.delete(`/saved-items/${id}`);
      refreshSavedCount();
    } catch {
      toast.error("Couldn't remove that item");
    }
  };

  const sorted = useMemo(() => {
    if (!items) return items;
    const arr = [...items];
    const name = (x) => (x.product_name || "").toLowerCase();
    const cat = (x) => (x.category || "General").toLowerCase();
    const time = (x) => new Date(x.created_at || 0).getTime();
    if (sort === "newest") arr.sort((a, b) => time(b) - time(a));
    else if (sort === "oldest") arr.sort((a, b) => time(a) - time(b));
    else if (sort === "az") arr.sort((a, b) => name(a).localeCompare(name(b)));
    else if (sort === "category") arr.sort((a, b) => cat(a).localeCompare(cat(b)) || name(a).localeCompare(name(b)));
    return arr;
  }, [items, sort]);

  const filteredVideos = useMemo(() => {
    if (!videos) return videos;
    const q = vQuery.trim().toLowerCase();
    if (!q) return videos;
    return videos.filter((v) => (v.title || "").toLowerCase().includes(q));
  }, [videos, vQuery]);

  return (
    <div className="max-w-7xl mx-auto px-4 pt-3 pb-24">
      <div className="flex items-center justify-between gap-2 mb-3">
        <h1 className="text-2xl sm:text-3xl font-extrabold flex items-center gap-2 min-w-0 text-white" data-testid="saved-page-title">
          <BrandName dark /> <span className="inline-flex items-center gap-2 whitespace-nowrap sms-outline-dark">SAVED VIDEOS <Bookmark size={22} className="text-white shrink-0" /></span>
        </h1>
        <MobileCreateButton />
        {items && items.length > 0 && (
          <div className="shrink-0 hidden sm:block">
            <div className="flex items-center gap-1.5 text-xs font-semibold text-[#9a96ac] mb-1 justify-end">
              <ArrowUpDown size={13} /> Sort
            </div>
            <select
              data-testid="buy-later-sort"
              value={sort}
              onChange={(e) => setSort(e.target.value)}
              className="text-sm font-bold text-white bg-black border-2 border-[#5d8a52] rounded-full px-4 py-2 outline-none cursor-pointer"
            >
              <option value="newest">Newest</option>
              <option value="oldest">Oldest</option>
              <option value="az">A–Z</option>
              <option value="category">Category</option>
            </select>
          </div>
        )}
      </div>

      {items && items.length > 0 && (
        <div className="sm:hidden flex justify-end mb-3">
          <select
            data-testid="buy-later-sort-m"
            value={sort}
            onChange={(e) => setSort(e.target.value)}
            className="text-sm font-bold text-white bg-black border-2 border-[#5d8a52] rounded-full px-4 py-2 outline-none cursor-pointer"
          >
            <option value="newest">Sort: Newest</option>
            <option value="oldest">Sort: Oldest</option>
            <option value="az">Sort: A–Z</option>
            <option value="category">Sort: Category</option>
          </select>
        </div>
      )}
      {videos && videos.length > 0 && (
        <div className="relative mb-6">
          <Search size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-white" />
          <input
            data-testid="saved-videos-search"
            value={vQuery}
            onChange={(e) => setVQuery(e.target.value)}
            placeholder="Search your saved videos…"
            className="w-full text-sm text-white placeholder:text-white/60 bg-black border-2 border-[#5d8a52] rounded-full pl-9 pr-9 py-2.5 outline-none focus:border-[#1e3aaa] transition-colors"
          />
          {vQuery && (
            <button data-testid="saved-videos-search-clear" onClick={() => setVQuery("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#b7b3c8] hover:text-[#6b6880]"><X size={15} /></button>
          )}
        </div>
      )}

      {/* Saved videos */}
      {videos && videos.length > 0 && (
        <div className="mb-8" data-testid="saved-videos-section">
          <div className="flex items-center gap-2 mb-3">
            <Play size={16} className="text-[#5d8a52]" fill="#5d8a52" />
            <h2 className="text-base font-extrabold text-white">Saved videos</h2>
            <span className="text-xs font-semibold text-[#9a96ac]">· {vQuery ? `${filteredVideos.length} of ${videos.length}` : videos.length}</span>
          </div>
          {filteredVideos.length === 0 ? (
            <div className="rounded-3xl p-8 text-center text-white/60" style={{ background: "#0f0f16", border: "2px solid #5d8a52" }} data-testid="saved-videos-no-results">Nothing matches “{vQuery}”.</div>
          ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-4 xl:grid-cols-4 gap-2.5 sm:gap-3">
            {filteredVideos.map((v, i) => (
              <div
                key={v.manual_id}
                data-testid={`saved-video-${v.manual_id}`}
                onClick={() => navigate(`/community/${v.manual_id}`)}
                className="sms-card sms-dark-page community-pop overflow-hidden text-left group sms-fade-up cursor-pointer"
                style={{ animationDelay: `${i * 0.03}s` }}
              >
                <div className="w-full aspect-video bg-[#100d1c] overflow-hidden relative">
                  {v.hero_image_id ? (
                    <img src={imageUrl(v.hero_image_id)} alt={v.title} loading="lazy"
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-[#b7b3c8]"><ImageOff size={26} /></div>
                  )}
                  <button
                    data-testid={`remove-saved-video-${v.manual_id}`}
                    onClick={(e) => { e.stopPropagation(); removeVideo(v.manual_id); }}
                    title="Remove from saved"
                    className="absolute bottom-2 right-2 p-1.5 rounded-full bg-black/45 text-white/90 hover:bg-black/70 hover:text-white transition-colors"
                  >
                    <Trash2 size={13} />
                  </button>
                </div>
                <div className="p-2.5">
                  <div className="flex items-center justify-between gap-1">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="text-[11px] font-bold uppercase tracking-wide text-[#ffd21e] shrink-0">{v.category}</span>
                      <span className="flex items-center gap-1 text-[11px] font-semibold text-[#9a96ac] shrink-0">
                        <Eye size={12} /> {formatViews(v.views)}
                      </span>
                      {v.comment_count > 0 && (
                        <span className="flex items-center gap-1 text-[11px] font-semibold text-[#5d8a52] shrink-0">
                          <MessageCircle size={12} /> {v.comment_count}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-1.5 shrink-0">
                      {STYLE_META[v.style] && (
                        <span className="flex items-center gap-1 text-[10px] font-extrabold text-[#3f6238] bg-[#eef3ea] px-2 py-0.5 rounded-full shrink-0">
                          <span className="text-[11px] leading-none">{STYLE_META[v.style].e}</span>
                          <span className="uppercase tracking-wide">{STYLE_META[v.style].l}</span>
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-start justify-between gap-1.5 mt-0.5">
                    <div className="font-bold text-[13px] leading-snug line-clamp-2 min-h-[2.4em] flex-1">{v.title}</div>
                    <button
                      data-testid={`saved-share-${v.manual_id}`}
                      onClick={(e) => shareCard(e, v)}
                      title="Share this video"
                      aria-label="Share this video"
                      className="shrink-0 -mr-0.5 -mt-0.5 p-1 text-[#171426] hover:text-[#4f7746] transition-colors"
                    >
                      <Share2 size={16} />
                    </button>
                  </div>
                  <div className="flex items-center justify-between gap-2 mt-1.5">
                    <StarRating value={v.rating_avg} count={v.rating_count} size={13} />
                    <div className="flex items-center gap-1.5 shrink-0">
                      <AudienceBadge audience={v.audience} topic={v.title} compact />
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
          )}
        </div>
      )}

      {items === null ? (
        <div className="py-24 flex flex-col items-center gap-3 text-[#6b6880]">
          <Loader2 size={28} className="spin text-[#5d8a52]" />
          <p className="font-medium">Loading your saved items…</p>
        </div>
      ) : items.length === 0 ? (
        (videos && videos.length > 0) ? null : (
        <div className="rounded-3xl p-10 text-center" style={{ background: "#0f0f16", border: "2px solid #5d8a52" }} data-testid="buy-later-empty">
          <Bookmark size={30} className="text-[#5d8a52] mx-auto mb-3" />
          <p className="text-white font-medium">Nothing saved yet.</p>
          <p className="text-white/50 text-sm mt-1">
            Tap “Save” on any video, or “Buy Later” on a recommended product, to keep it here.
          </p>
        </div>
        )
      ) : (
        <>
          {videos && videos.length > 0 && (
            <div className="flex items-center gap-2 mb-3">
              <Bookmark size={16} className="text-[#5d8a52]" />
              <h2 className="text-base font-extrabold text-white">Buy-later products</h2>
              <span className="text-xs font-semibold text-[#9a96ac]">· {items.length}</span>
            </div>
          )}
          <div className="rounded-2xl border-2 border-[#5d8a52] overflow-hidden sms-tweed" data-testid="buy-later-list">
            {sorted.map((it, i) => {
              const store = it.store || "Amazon";
              return (
                <div
                  key={it.id}
                  data-testid={`saved-item-${i}`}
                  className="flex items-center gap-3 px-4 py-3 border-b border-white/10 last:border-b-0"
                  style={{ background: i % 2 === 0 ? "rgba(255,255,255,.03)" : "transparent" }}
                >
                  <span className="text-sm font-extrabold text-[#b7add6] w-6 shrink-0 text-center tabular-nums">
                    {i + 1}
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="font-bold text-white truncate">{it.product_name}</div>
                    <div className="text-xs text-[#9a96ac] truncate">
                      {[it.category, it.supply, it.price_range, it.manual_title && `from “${it.manual_title}”`]
                        .filter(Boolean)
                        .join(" · ")}
                    </div>
                  </div>
                  <a
                    data-testid={`saved-buy-${i}`}
                    href={buildItemUrl(store, it.search_query || it.product_name, amazonTag)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 font-bold text-sm text-black bg-[#ffd814] hover:bg-[#f7ca00] px-4 py-2 rounded-full transition-colors shrink-0"
                  >
                    <ExternalLink size={15} /> Buy
                  </a>
                  <button
                    data-testid={`saved-remove-${i}`}
                    onClick={() => remove(it.id)}
                    aria-label="Remove"
                    className="p-2 rounded-full text-[#a9c39c] hover:text-[#e0455c] hover:bg-[#fdeef0] transition-colors shrink-0"
                  >
                    <Trash2 size={18} />
                  </button>
                </div>
              );
            })}
          </div>
          <p className="text-center text-xs text-[#b7b3c8] mt-4 px-4" data-testid="amazon-disclosure">
            As an Amazon Associate, Vittyyo earns from qualifying purchases.
          </p>
        </>
      )}
    </div>
  );
}
